import React, { useEffect, useState, useCallback, useRef, forwardRef, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X,
  Package,
  Truck,
  Search,
  Plus,
  ArrowLeft,
  Barcode,
  Box,
  AlertCircle,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Download,
  ChevronDown,
  History,
  Printer,
  FileSpreadsheet,
  Eraser,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { FixedSizeList as FixedList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import commonStyles from '../../styles/common.module.css';
import styles from './PackagingDetails.module.css';
import EquipmentHistoryPanel from '../EquipmentHistory/EquipmentHistoryPanel';
import useEquipmentRealtime from '../../hooks/useEquipmentRealtime';
import { shouldSendToTelegram } from '../../utils/telegram';

const MAX_EQUIPMENTS_PER_BOX = 60;
const PAGE_SIZE = 50;

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
}[character]));

const openPrintWindow = (title, body, pageCss = 'size: A4; margin: 12mm;', labelCss = '') => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Браузер заблокировал окно печати. Разрешите всплывающие окна для CRM.');
    return;
  }
  printWindow.opener = null;
  printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    @page { ${pageCss} } * { box-sizing: border-box; } body { margin: 0; color: #111827; font-family: Arial, sans-serif; }
    .label { border: 2px solid #111827; padding: 10mm; display: grid; align-content: start; gap: 8px; overflow-wrap: anywhere; break-inside: avoid; }
    h1 { margin: 0 0 4mm; font-size: 20pt; } p { margin: 0; font-size: 12pt; line-height: 1.35; }
    .big { margin-top: 5mm; font-size: 26pt; font-weight: 800; } table { width: 100%; border-collapse: collapse; } th, td { padding: 6px; border: 1px solid #94a3b8; text-align: left; font-size: 10pt; }
    ${labelCss}
    @media print { .no-print { display: none !important; } }
  </style></head><body>${body}<script>window.addEventListener('load',()=>{window.print();});</script></body></html>`);
  printWindow.document.close();
};

const DropdownPortal = ({ children, parentRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    }
  }, [parentRef]);

  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: position.top, left: position.left, zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );
};

const ReportDropdown = ({ boxId, reportTemplates, handleDownloadReport, customerId }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleTemplateClick = (template, e) => {
    e.stopPropagation();
    setOpen(false);
    handleDownloadReport(template, boxId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const applicableTemplates = reportTemplates.filter(
    (template) => 
      (template.customer === null || (template.customer && template.customer.id === customerId)) &&
      template.target_pages.includes('packaging')
  );

  return (
    <div className={styles.dropdownContainer}>
      <button ref={triggerRef} className={styles.dropdownTrigger} onClick={toggleDropdown}>
        <Download size={16} />
        <ChevronDown size={14} className={styles.chevron} />
      </button>
      {open && (
        <DropdownPortal parentRef={triggerRef}>
          <div ref={menuRef} className={styles.dropdownMenu}>
            {applicableTemplates.length > 0 ? (
              applicableTemplates.map((template) => (
                <button
                  key={template.id}
                  className={styles.dropdownItem}
                  onClick={(e) => handleTemplateClick(template, e)}
                >
                  <Download size={14} className={styles.dropdownItemIcon} />
                  {template.name}
                </button>
              ))
            ) : (
              <div className={styles.dropdownItem}>Нет доступных шаблонов</div>
            )}
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};

const PackagingDetails = forwardRef(({ customerId: propCustomerId, cityId: propCityId }, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const repairRequestId = query.get('id');
  const groupCustomerId = propCustomerId || query.get('customer_id');
  const groupCityId = propCityId || query.get('city_id');
  const isPanel = !!(propCustomerId && propCityId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [repairRequest, setRepairRequest] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState(null);
  const [packagedEquipments, setPackagedEquipments] = useState([]);
  const [readyEquipments, setReadyEquipments] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [scannedSerial, setScannedSerial] = useState('');
  const [packagedFilter, setPackagedFilter] = useState('');
  const [readyFilter, setReadyFilter] = useState('');
  const [readyPage, setReadyPage] = useState(1);
  const [packagedPage, setPackagedPage] = useState(1);
  const [hasMoreReady, setHasMoreReady] = useState(true);
  const [hasMorePackaged, setHasMorePackaged] = useState(true);
  const [isBoxCreating, setIsBoxCreating] = useState(false);
  const [isBoxDeleting, setIsBoxDeleting] = useState(false);
  const [isBoxClosing, setIsBoxClosing] = useState(false);
  const [isBoxOpening, setIsBoxOpening] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [boxesPage, setBoxesPage] = useState(0);
  const [isPackagedPanelOpen, setIsPackagedPanelOpen] = useState(false);
  const [boxesPerPage, setBoxesPerPage] = useState(2);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [shipmentDocuments, setShipmentDocuments] = useState(null);
  const [shipmentDocumentsLoading, setShipmentDocumentsLoading] = useState(false);

  const serialInputRef = useRef(null);
  const scanningRef = useRef(false);
  const readyListContainerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const pendingRealtimeRef = useRef(false);
  const selectedBoxRef = useRef(null);
  const ignoreRealtimeRef = useRef(false);
  const realtimeTimerRef = useRef(null);

  useEffect(() => {
    selectedBoxRef.current = selectedBox;
  }, [selectedBox]);

  useEffect(() => () => {
    if (realtimeTimerRef.current) {
      clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = null;
    }
  }, []);

  const suppressRealtime = useCallback((delay = 400) => {
    ignoreRealtimeRef.current = true;
    if (realtimeTimerRef.current) {
      clearTimeout(realtimeTimerRef.current);
    }
    realtimeTimerRef.current = setTimeout(() => {
      ignoreRealtimeRef.current = false;
      realtimeTimerRef.current = null;
    }, delay);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setBoxesPerPage(1);
      } else if (window.innerWidth < 1200) {
        setBoxesPerPage(2);
      } else {
        setBoxesPerPage(2);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (serialInputRef.current) {
      serialInputRef.current.focus();
    }
  }, [selectedBox]);

  useEffect(() => {
    if (scanError) {
      const timer = setTimeout(() => {
        setScanError(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [scanError]);

  const fetchReportTemplates = async () => {
    try {
      const selectedProjectId = localStorage.getItem('selected_project_id');
      const response = await api.get('/reports/templates/', {
        params: {
          project_id: selectedProjectId,
          target_page: 'packaging', // Оставляем для фильтрации по одной странице
        },
      });
      setReportTemplates(response.data || []);
    } catch (err) {
      console.error('fetchReportTemplates error:', err);
      toast.error('Ошибка загрузки шаблонов отчётов', { toastId: 'fetch-templates-error', autoClose: 3000 });
    }
  };

  const handleDownloadReport = async (template, boxId) => {
    try {
      const selectedProjectId = localStorage.getItem('selected_project_id');
      const params = {
        template_id: template.id,
        box_id: boxId,
        project_id: selectedProjectId,
      };

      if (shouldSendToTelegram()) {
        try {
          await api.get('/reports/generate/', {
            params: { ...params, send_to_telegram: true },
          });
          toast.success('Отчёт отправлен в Telegram', { toastId: 'download-report-success', autoClose: 2000 });
          return;
        } catch (err) {
          console.warn('Telegram send failed, fallback to download:', err);
        }
      }

      const response = await api.get('/reports/generate/', {
        params,
        responseType: 'blob',
      });
      const filename = `report_box_${boxId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Отчёт успешно скачан', { toastId: 'download-report-success', autoClose: 2000 });
    } catch (err) {
      console.error('handleDownloadReport error:', err);
      toast.error('Ошибка при скачивании отчёта', { toastId: 'download-report-error', autoClose: 3000 });
    }
  };

  const handleSerialInputChange = (e) => {
    const value = e.target.value;
    setScannedSerial(value);

    const exactMatch = readyEquipments.find(
      (eq) => eq.serial_number.toLowerCase() === value.toLowerCase()
    );

    if (exactMatch && selectedBox && selectedBox.is_open) {
      handleScanSerial(value);
    }
  };

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) {
      pendingRealtimeRef.current = true;
      return;
    }
    isFetchingRef.current = true;
    if (
      !repairRequestId &&
      (!groupCustomerId || !groupCityId || groupCustomerId === 'undefined' || groupCityId === 'undefined')
    ) {
      setError('Не переданы необходимые параметры');
      console.error('fetchData: Missing required parameters', {
        repairRequestId,
        groupCustomerId,
        groupCityId,
      });
      setLoading(false);
      isFetchingRef.current = false;
      return;
    }

    setLoading(true);
    try {
      if (repairRequestId) {
        const [repairResponse, boxesResponse] = await Promise.all([
          api.get(`/repairs/repair-requests/${repairRequestId}/`, {
            params: { with_details: true },
          }),
          api.get('/repairs/boxes/', {
            params: {
              repair_request: repairRequestId,
              project_id: localStorage.getItem('selected_project_id'),
            },
          }),
        ]);

        const repairRequestData = repairResponse.data;
        const customerId =
          typeof repairRequestData.customer === 'object' ? repairRequestData.customer.id : repairRequestData.customer;
        const cityId = repairRequestData.territory?.city?.id || repairRequestData.service_center?.city?.id;

        if (!customerId || !cityId) {
          throw new Error('Отсутствует customer_id или city_id в заявке');
        }

        const allBoxes = Array.isArray(boxesResponse.data.results) ? boxesResponse.data.results : boxesResponse.data;
        const filteredBoxes = allBoxes.filter(
          (box) =>
            !box.packaged_equipment?.some(
              (eq) => eq.location_status === 'shipped' || eq.location_status === 'awaiting_shipping'
            )
        );

        setRepairRequest(repairRequestData);
        setBoxes(filteredBoxes);
        const prevSelectedId = selectedBoxRef.current?.id;
        let boxToSelect = null;
        if (prevSelectedId) {
          boxToSelect = filteredBoxes.find((box) => box.id === prevSelectedId) || null;
        }
        if (!boxToSelect) {
          const open = filteredBoxes.find((box) => box.is_open);
          boxToSelect = open || (filteredBoxes.length > 0 ? filteredBoxes[0] : null);
        }
        setSelectedBox(boxToSelect);
        setPackagedEquipments((boxToSelect && boxToSelect.packaged_equipment) || []);

        const equipmentResponse = await api.get('/repairs/equipments/list_packaging/', {
          params: {
            repair_request: repairRequestId,
            location_status: 'in_packaging',
            project_id: localStorage.getItem('selected_project_id'),
            page: 1,
            page_size: PAGE_SIZE,
          },
        });

        setReadyEquipments(equipmentResponse.data.results || []);
        setHasMoreReady(equipmentResponse.data.next !== null);
      } else {
        const [boxesResponse, customerResponse, cityResponse] = await Promise.all([
          api.get('/repairs/boxes/', {
            params: {
              repair_request: null,
              customer: groupCustomerId,
              city: groupCityId,
              project_id: localStorage.getItem('selected_project_id'),
            },
          }),
          api.get(`/core/customers/${groupCustomerId}/`).catch((err) => ({
            data: { id: groupCustomerId, name: `Заказчик с ID ${groupCustomerId}` },
          })),
          api.get(`/core/cities/${groupCityId}/`).catch((err) => ({
            data: { id: groupCityId, name: `Город с ID ${groupCityId}` },
          })),
        ]);

        const customerData = customerResponse.data;
        const cityData = cityResponse.data;
        const customerName = customerData.name || `Заказчик с ID ${groupCustomerId}`;
        const cityName = cityData.name || `Город с ID ${groupCityId}`;

        setRepairRequest({
          request_number: `Группа: ${customerName} | ${cityName}`,
          customer: { id: groupCustomerId, name: customerName },
          territory: { city: { id: groupCityId, name: cityName } },
          service_center: { city: { id: groupCityId, name: cityName } },
        });

        const allBoxes = Array.isArray(boxesResponse.data.results) ? boxesResponse.data.results : boxesResponse.data;
        const filteredBoxes = allBoxes.filter(
          (box) =>
            !box.packaged_equipment?.some(
              (eq) => eq.location_status === 'shipped' || eq.location_status === 'awaiting_shipping'
            )
        );

        setBoxes(filteredBoxes);
        const prevSelectedId = selectedBoxRef.current?.id;
        let boxToSelect = null;
        if (prevSelectedId) {
          boxToSelect = filteredBoxes.find((box) => box.id === prevSelectedId) || null;
        }
        if (!boxToSelect) {
          const open = filteredBoxes.find((box) => box.is_open);
          boxToSelect = open || (filteredBoxes.length > 0 ? filteredBoxes[0] : null);
        }
        setSelectedBox(boxToSelect);
        setPackagedEquipments((boxToSelect && boxToSelect.packaged_equipment) || []);

        const equipmentResponse = await api.get('/repairs/equipments/list_packaging/', {
          params: {
            customer: groupCustomerId,
            city: groupCityId,
            location_status: 'in_packaging',
            project_id: localStorage.getItem('selected_project_id'),
            page: 1,
            page_size: PAGE_SIZE,
          },
        });

        setReadyEquipments(equipmentResponse.data.results || []);
        setHasMoreReady(equipmentResponse.data.next !== null);
      }

      await fetchReportTemplates();
    } catch (err) {
      console.error('fetchData error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка загрузки данных';
      setError(errorMsg);
      toast.error(errorMsg, { toastId: 'fetch-data-error', autoClose: 3000 });
    } finally {
      setLoading(false);
      setSelectedBoxes([]);
      isFetchingRef.current = false;
      if (pendingRealtimeRef.current) {
        pendingRealtimeRef.current = false;
        fetchData();
      }
    }
  }, [repairRequestId, groupCustomerId, groupCityId]);

  const handleRealtimeMessage = useCallback(
    (payload) => {
      if (!payload) return;
      const { project_id: payloadProjectId, action, stage } = payload;
      const selectedProjectId = typeof window !== 'undefined' ? localStorage.getItem('selected_project_id') : null;
      if (selectedProjectId && payloadProjectId && Number(selectedProjectId) !== Number(payloadProjectId)) {
        return;
      }
      if (ignoreRealtimeRef.current) return;

      if (stage === 'packaging_boxes' && action === 'packaging_boxes_changed') {
        fetchData();
        return;
      }
      if (!['equipment_changed', 'equipment_removed'].includes(action)) return;

      const equipmentPayload = payload.equipment;
      let relevant = false;

      if (repairRequestId) {
        const requestIdNum = Number(repairRequestId);
        if (Number.isNaN(requestIdNum)) {
          relevant = true;
        } else if (!equipmentPayload) {
          relevant = true;
        } else if (equipmentPayload.repair_request?.id === requestIdNum) {
          relevant = true;
        }
      } else if (groupCustomerId || groupCityId) {
        if (!equipmentPayload) {
          relevant = true;
        } else {
          let matches = true;
          if (groupCustomerId) {
            const customerIdNum = Number(groupCustomerId);
            if (!Number.isNaN(customerIdNum) && equipmentPayload.customer_id !== customerIdNum) {
              matches = false;
            }
          }
          if (matches && groupCityId) {
            const cityIdNum = Number(groupCityId);
            if (!Number.isNaN(cityIdNum)) {
              const equipmentCityId = equipmentPayload.territory_city_id || equipmentPayload.service_center_city_id;
              if (equipmentCityId !== cityIdNum) {
                matches = false;
              }
            }
          }
          relevant = matches;
        }
      } else {
        relevant = true;
      }

      if (relevant) {
        if (isFetchingRef.current) {
          pendingRealtimeRef.current = true;
          return;
        }
        fetchData();
      }
    },
    [fetchData, repairRequestId, groupCustomerId, groupCityId]
  );

useEquipmentRealtime('in_packaging', handleRealtimeMessage);
useEquipmentRealtime('packaged', handleRealtimeMessage);
useEquipmentRealtime('packaging_boxes', handleRealtimeMessage);

  const loadMoreReadyEquipments = useCallback(async () => {
    if (!hasMoreReady || loading) return;
    setLoading(true);
    try {
      const nextPage = readyPage + 1;
      const params = {
        location_status: 'in_packaging',
        project_id: localStorage.getItem('selected_project_id'),
        page: nextPage,
        page_size: PAGE_SIZE,
      };
      if (repairRequestId) {
        params.repair_request = repairRequestId;
      } else {
        params.customer = groupCustomerId;
        params.city = groupCityId;
      }
      const response = await api.get('/repairs/equipments/list_packaging/', { params });
      setReadyEquipments((prev) => [...prev, ...(response.data.results || [])]);
      setHasMoreReady(response.data.next !== null);
      setReadyPage(nextPage);
    } catch (err) {
      console.error('loadMoreReadyEquipments error:', err);
      toast.error('Ошибка загрузки дополнительного оборудования', { toastId: 'load-more-error', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [hasMoreReady, loading, readyPage, repairRequestId, groupCustomerId, groupCityId]);

  const updateSelectedBox = async () => {
    if (selectedBox) {
      try {
        const response = await api.get(`/repairs/boxes/${selectedBox.id}/`, {
          params: { page: 1, page_size: PAGE_SIZE },
        });
        const updatedBox = response.data;
        setSelectedBox(updatedBox);
        setPackagedEquipments(updatedBox.packaged_equipment?.filter((eq) => eq.location_status === 'packaged') || []);
        setPackagedPage(1);
        setHasMorePackaged(updatedBox.packaged_equipment?.length >= PAGE_SIZE);
        setBoxes((prevBoxes) =>
          prevBoxes.map((box) => (box.id === updatedBox.id ? updatedBox : box))
        );
      } catch (err) {
        console.error('updateSelectedBox error:', err);
        const errorMsg = err.response?.error || 'Ошибка обновления данных коробки';
        toast.error(errorMsg, { toastId: 'update-box-error', autoClose: 3000 });
      }
    }
  };

  const loadMorePackagedEquipments = useCallback(async () => {
    if (!hasMorePackaged || loading || !selectedBox) return;
    setLoading(true);
    try {
      const nextPage = packagedPage + 1;
      const response = await api.get(`/repairs/boxes/${selectedBox.id}/`, {
        params: { page: nextPage, page_size: PAGE_SIZE },
      });
      const updatedBox = response.data;
      setPackagedEquipments((prev) => [
        ...prev,
        ...(updatedBox.packaged_equipment?.filter((eq) => eq.location_status === 'packaged') || []),
      ]);
      setHasMorePackaged(updatedBox.packaged_equipment?.length >= PAGE_SIZE);
      setPackagedPage(nextPage);
    } catch (err) {
      console.error('loadMorePackagedEquipments error:', err);
      toast.error('Ошибка загрузки оборудования коробки', { toastId: 'load-packaged-error', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, [hasMorePackaged, loading, packagedPage, selectedBox]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBoxSelect = (boxId) => {
    const box = boxes.find((b) => b.id === boxId);
    setSelectedBox(box);
    setPackagedEquipments(box ? (box.packaged_equipment?.filter((eq) => eq.location_status === 'packaged') || []) : []);

    setTimeout(() => {
      if (serialInputRef.current) {
        serialInputRef.current.focus();
      }
    }, 100);
  };

  const handleCheckboxChange = (boxId) => {
    setSelectedBoxes((prev) =>
      prev.includes(boxId) ? prev.filter((id) => id !== boxId) : [...prev, boxId]
    );
  };

  const shipSingleBox = async () => {
    if (!selectedBox) {
      toast.error('Выберите коробку', { toastId: 'ship-single-no-selection', autoClose: 3000 });
      return;
    }
    if (selectedBox.is_open) {
      toast.error('Коробка должна быть закрыта перед отправкой', {
        toastId: 'ship-single-open',
        autoClose: 3000,
      });
      return;
    }

    suppressRealtime();
    try {
      const response = await api.post(`/repairs/boxes/${selectedBox.id}/ship_single/`);
      toast.success('Коробка передана на отправку', { toastId: 'ship-single-success', autoClose: 2000 });
      await loadShipmentDocuments(response.data.batch_number);
      await fetchData();
    } catch (err) {
      console.error('shipSingleBox error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка передачи коробки на отправку';
      toast.error(errorMsg, { toastId: 'ship-single-error', autoClose: 3000 });
    }
  };

  const shipSelectedBoxes = async () => {
    if (selectedBoxes.length === 0) {
      toast.error('Выберите хотя бы одну коробку', { toastId: 'ship-selected-no-selection', autoClose: 3000 });
      return;
    }

    const ineligible = boxes.filter((b) => selectedBoxes.includes(b.id) && b.is_open);
    if (ineligible.length > 0) {
      toast.error('Все выбранные коробки должны быть закрыты', {
        toastId: 'ship-selected-open',
        autoClose: 3000,
      });
      return;
    }

    suppressRealtime();
    try {
      const payload = { box_ids: selectedBoxes };
      const response = await api.post('/repairs/boxes/ship_selected/', payload);
      toast.success('Выбранные коробки переданы на отправку', {
        toastId: 'ship-selected-success',
        autoClose: 2000,
      });
      await loadShipmentDocuments(response.data.batch_number);
      await fetchData();
    } catch (err) {
      console.error('shipSelectedBoxes error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка передачи выбранных коробок на отправку';
      toast.error(errorMsg, { toastId: 'ship-selected-error', autoClose: 3000 });
    }
  };

  const closeSelectedBox = async () => {
    if (!selectedBox) {
      toast.error('Сначала выберите коробку', { toastId: 'close-box-no-selection', autoClose: 3000 });
      return;
    }

    suppressRealtime();
    setIsBoxClosing(true);
    try {
      await api.post(`/repairs/boxes/${selectedBox.id}/close_box/`);
      setSelectedBox((prev) => (prev ? { ...prev, is_open: false, closed_at: new Date().toISOString() } : prev));
      await fetchData();
      toast.success('Коробка успешно закрыта', { toastId: 'close-box-success', autoClose: 2000 });
    } catch (err) {
      console.error('closeSelectedBox error:', err);
      const errorMsg = err.response?.data?.detail || 'Ошибка закрытия коробки';
      toast.error(errorMsg, { toastId: 'close-box-error', autoClose: 3000 });
      await fetchData();
    } finally {
      setIsBoxClosing(false);
    }
  };

  const openSelectedBox = async () => {
    if (!selectedBox) {
      toast.error('Сначала выберите коробку', { toastId: 'open-box-no-selection', autoClose: 3000 });
      return;
    }

    suppressRealtime();
    setIsBoxOpening(true);
    try {
      await api.post(`/repairs/boxes/${selectedBox.id}/open_box/`);
      setSelectedBox((prev) => (prev ? { ...prev, is_open: true, closed_at: null } : prev));
      await fetchData();
      toast.success('Коробка успешно открыта', { toastId: 'open-box-success', autoClose: 2000 });
    } catch (err) {
      console.error('openSelectedBox error:', err);
      const errorMsg = err.response?.data?.detail || 'Ошибка открытия коробки';
      toast.error(errorMsg, { toastId: 'open-box-error', autoClose: 3000 });
      await fetchData();
    } finally {
      setIsBoxOpening(false);
    }
  };

  const deleteEmptyBox = async () => {
    if (!selectedBox) {
      toast.error('Сначала выберите коробку', { toastId: 'delete-box-no-selection', autoClose: 3000 });
      return;
    }
    if (packagedEquipments.length > 0) {
      toast.error('Нельзя удалить коробку, содержащую оборудование', {
        toastId: 'delete-box-not-empty',
        autoClose: 3000,
      });
      return;
    }
    if (!window.confirm('Вы уверены, что хотите удалить пустую коробку?')) return;

    suppressRealtime();
    setIsBoxDeleting(true);
    try {
      await api.delete(`/repairs/boxes/${selectedBox.id}/delete_empty/`);
      await fetchData();
      setSelectedBox(null);
      setPackagedEquipments([]);
      toast.success('Пустая коробка успешно удалена', { toastId: 'delete-box-success', autoClose: 2000 });
    } catch (err) {
      console.error('deleteEmptyBox error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка удаления коробки';
      toast.error(errorMsg, { toastId: 'delete-box-error', autoClose: 3000 });
    } finally {
      setIsBoxDeleting(false);
    }
  };

  const createBox = async () => {
    let payload = {};
    if (repairRequestId) {
      const customerId =
        typeof repairRequest.customer === 'object' && repairRequest.customer.id
          ? repairRequest.customer.id
          : repairRequest.customer;
      if (!customerId) {
        toast.error('Ошибка: в заявке отсутствует идентификатор заказчика', {
          toastId: 'create-box-no-customer',
          autoClose: 3000,
        });
        return;
      }
      payload = {
        repair_request_id: parseInt(repairRequestId, 10),
        customer_id: customerId,
        city_id: repairRequest.territory
          ? repairRequest.territory.city.id
          : repairRequest.service_center.city.id,
      };
    } else {
      if (
        !groupCustomerId ||
        !groupCityId ||
        groupCustomerId === 'undefined' ||
        groupCityId === 'undefined'
      ) {
        toast.error('В групповом режиме необходимо передать корректные customer_id и city_id', {
          toastId: 'create-box-group-params',
          autoClose: 3000,
        });
        return;
      }
      const customerId = parseInt(groupCustomerId, 10);
      const cityId = parseInt(groupCityId, 10);
      if (isNaN(customerId) || isNaN(cityId)) {
        toast.error('Некорректные customer_id или city_id', {
          toastId: 'create-box-invalid-params',
          autoClose: 3000,
        });
        return;
      }
      payload = {
        repair_request_id: null,
        customer_id: customerId,
        city_id: cityId,
      };
    }

    suppressRealtime();
    setIsBoxCreating(true);
    try {
      const response = await api.post('/repairs/boxes/create_box/', payload);
      setSelectedBox(response.data.box);
      await fetchData();
      toast.success('Новая коробка успешно создана', { toastId: 'create-box-success', autoClose: 2000 });
    } catch (err) {
      console.error('createBox error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка создания коробки';
      toast.error(errorMsg, { toastId: 'create-box-error', autoClose: 3000 });
    } finally {
      setIsBoxCreating(false);
    }
  };

  const handleScanSerial = async (serialToScan = null) => {
    if (scanningRef.current) return;
    const trimmedSerial = serialToScan || scannedSerial.trim();
    if (!trimmedSerial) {
      toast.error('Введите серийный номер', { toastId: 'scan-serial-empty', autoClose: 3000 });
      setScanError(true);
      return;
    }
    if (!selectedBox) {
      toast.error('Выберите коробку для упаковки', { toastId: 'scan-serial-no-box', autoClose: 3000 });
      setScanError(true);
      return;
    }
    if (!selectedBox.is_open) {
      toast.error('Коробка закрыта. Откройте коробку для сканирования', {
        toastId: 'scan-serial-box-closed',
        autoClose: 3000,
      });
      setScanError(true);
      return;
    }

    scanningRef.current = true;
    suppressRealtime();
    setIsScanning(true);
    try {
      const payload = {
        serial: trimmedSerial,
        box_id: selectedBox.id,
      };
      const response = await api.post('/repairs/equipments/package_equipment/', payload);
      const equipment = response.data.equipment;
      const addToBox = (box) => {
        if (!box || box.id !== payload.box_id) return box;
        const current = box.packaged_equipment || [];
        if (current.some((eq) => eq.id === equipment.id)) return box;
        return { ...box, packaged_equipment: [...current, equipment] };
      };
      setSelectedBox(addToBox);
      setBoxes((prev) => prev.map(addToBox));
      if (selectedBoxRef.current?.id === payload.box_id) {
        setPackagedEquipments((prev) => (
          prev.some((eq) => eq.id === equipment.id) ? prev : [...prev, equipment]
        ));
      }
      setReadyEquipments((prev) => prev.filter((eq) => eq.id !== equipment.id));
      setScannedSerial('');
      toast.success(`Оборудование ${trimmedSerial} упаковано`, {
        toastId: `scan-serial-${trimmedSerial}`,
        autoClose: 3000,
      });

      if (serialInputRef.current) {
        serialInputRef.current.focus();
      }
    } catch (err) {
      console.error('handleScanSerial error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка упаковки оборудования';
      toast.error(errorMsg, { toastId: 'scan-serial-error', autoClose: 3000 });
      setScanError(true);
      const status = err.response?.status;
      if (
        status === 404 ||
        (typeof errorMsg === 'string' && /коробка.*не найдена/i.test(errorMsg))
      ) {
        await fetchData();
      }
    } finally {
      scanningRef.current = false;
      setIsScanning(false);
    }
  };

  const handleRemoveEquipment = async (equipmentId) => {
    if (!selectedBox) {
      toast.error('Выберите коробку', { toastId: 'remove-equipment-no-box', autoClose: 3000 });
      return;
    }
    suppressRealtime();
    try {
      const payload = {
        equipment_id: equipmentId,
        box_id: selectedBox.id,
      };
      const response = await api.post('/repairs/equipments/unpackage/', payload);
      await updateSelectedBox();
      setReadyEquipments((prev) => [...prev, response.data.equipment]);
      toast.success('Оборудование удалено из коробки', {
        toastId: `remove-equipment-${equipmentId}`,
        autoClose: 3000,
      });
    } catch (err) {
      console.error('handleRemoveEquipment error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка удаления оборудования из коробки';
      toast.error(errorMsg, { toastId: 'remove-equipment-error', autoClose: 3000 });
    }
  };

  const handleEmptyBox = async () => {
    if (!selectedBox?.id) return;
    if (!selectedBox.is_open) {
      toast.error('Сначала откройте коробку');
      return;
    }
    const count = filteredPackagedEquipments.length;
    if (!count || !window.confirm(`Убрать из коробки всё оборудование (${count} шт.)?`)) return;
    suppressRealtime();
    try {
      const { data } = await api.post(`/repairs/boxes/${selectedBox.id}/empty_box/`);
      toast.success(`Из коробки убрано: ${data.removed_count || 0}`);
      setIsPackagedPanelOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось очистить коробку');
    }
  };

  const loadShipmentDocuments = async (batchNumber) => {
    if (!batchNumber) return;
    setShipmentDocumentsLoading(true);
    try {
      const { data } = await api.get('/repairs/boxes/shipment_documents/', {
        params: { batch_number: batchNumber },
      });
      setShipmentDocuments(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Коробки переданы, но документы партии загрузить не удалось');
    } finally {
      setShipmentDocumentsLoading(false);
    }
  };

  const shipBoxes = async () => {
    const ineligibleBoxes = boxes.filter((box) => box.is_open);
    if (ineligibleBoxes.length > 0) {
      toast.error('Нельзя передать на отправку, пока есть открытые коробки', {
        toastId: 'ship-boxes-open',
        autoClose: 3000,
      });
      return;
    }
    suppressRealtime();
    try {
      const payload = {
        repair_request_id: repairRequestId || null,
        customer_id: repairRequestId
          ? typeof repairRequest.customer === 'object'
            ? repairRequest.customer.id
            : repairRequest.customer
          : groupCustomerId,
        city_id: repairRequestId
          ? repairRequest.territory?.city?.id || repairRequest.service_center?.city?.id
          : groupCityId,
      };
      const response = await api.post('/repairs/boxes/ship_boxes/', payload);
      toast.success('Коробки переданы на отправку', { toastId: 'ship-boxes-success', autoClose: 2000 });
      await loadShipmentDocuments(response.data.batch_number);
      await fetchData();
    } catch (err) {
      console.error('shipBoxes error:', err);
      const errorMsg = err.response?.data?.error || 'Ошибка передачи коробок на отправку';
      toast.error(errorMsg, { toastId: 'ship-boxes-error', autoClose: 3000 });
    }
  };

  const handleEquipmentHistoryClick = (equipmentId, e) => {
    e.stopPropagation();
    setSelectedEquipmentId(equipmentId);
    setHistoryPanelOpen(true);
  };

  const closeHistoryPanel = () => {
    setHistoryPanelOpen(false);
    setSelectedEquipmentId(null);
  };

  const filteredPackagedEquipments = packagedEquipments.filter(
    (eq) =>
      eq.serial_number.toLowerCase().includes(packagedFilter.toLowerCase()) ||
      eq.equipment_model.toLowerCase().includes(packagedFilter.toLowerCase())
  );

  const filteredReadyEquipments = readyEquipments.filter(
    (eq) =>
      eq.serial_number.toLowerCase().includes(readyFilter.toLowerCase()) ||
      eq.equipment_model.toLowerCase().includes(readyFilter.toLowerCase())
  );

  const maxBoxPage = Math.ceil(boxes.length / boxesPerPage) - 1;
  const visibleBoxes = boxes.slice(boxesPage * boxesPerPage, (boxesPage + 1) * boxesPerPage);

  const nextBoxPage = () => {
    if (boxesPage < maxBoxPage) {
      setBoxesPage(boxesPage + 1);
    }
  };

  const prevBoxPage = () => {
    if (boxesPage > 0) {
      setBoxesPage(boxesPage - 1);
    }
  };

  const EquipmentRow = ({ index, style, data }) => {
    const eq = data[index];
    return (
      <li key={eq.id} className={styles.listItem} style={{ ...style, height: 'auto', minHeight: '70px', padding: '8px 0' }}>
        <div className={styles.equipmentInfo}>
          <span className={styles.serialNumber}>{eq.serial_number}</span>
          <span className={styles.modelName}>{eq.equipment_model}</span>
          <span className={styles.statusBadge}>{eq.repair_status_display || 'Не установлен'}</span>
        </div>
        <div className={styles.equipmentActions}>
          <button
            className={styles.historyButton}
            onClick={(e) => handleEquipmentHistoryClick(eq.id, e)}
            title="Показать историю оборудования"
          >
            <History size={16} />
          </button>
        </div>
      </li>
    );
  };

  const handlePackagedPanelClose = useCallback(() => setIsPackagedPanelOpen(false), []);

  if (loading && !readyEquipments.length && !packagedEquipments.length) {
    return (
      <div className={commonStyles.loading}>
        <div className={commonStyles.spinner}></div>
      </div>
    );
  }
  if (error) return <div className={commonStyles.error}>{error}</div>;

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      <div className={styles.gridContainer}>
        <div className={styles.leftColumn}>
          <div className={styles.boxesSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.headerLeft}>
                <h2 className={styles.sectionTitle}>
                  <Box size={20} className={styles.sectionIcon} />
                  Коробки
                </h2>
                {!isPanel && (
                  <button className={styles.backButton} onClick={() => navigate('/packaging')}>
                    <ArrowLeft size={16} />
                    <span>Назад к списку</span>
                  </button>
                )}
              </div>
              <button
                className={`${styles.actionButton} ${styles.createButton}`}
                onClick={createBox}
                disabled={isBoxCreating}
              >
                {isBoxCreating ? (
                  <RefreshCw size={18} className={styles.spinIcon} />
                ) : (
                  <Plus size={18} />
                )}
                <span>Создать коробку</span>
              </button>
            </div>

            <div className={styles.boxesHeader}>
              <div className={styles.headerInfo}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Заказчик</span>
                  <span className={styles.infoValue}>{repairRequest?.customer?.name || 'Не указан'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Город</span>
                  <span className={styles.infoValue}>
                    {repairRequest?.territory?.city?.name ||
                      repairRequest?.service_center?.city?.name ||
                      'Не указан'}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Заявка</span>
                  <span className={styles.infoValue}>{repairRequest?.request_number || 'Не указана'}</span>
                </div>
              </div>
            </div>

            {boxes.length > 0 ? (
              <div className={styles.boxesContainer}>
                {boxesPage > 0 && (
                  <button
                    className={styles.boxesNavButton}
                    onClick={prevBoxPage}
                    aria-label="Предыдущие коробки"
                  >
                    <ChevronLeft size={24} />
                  </button>
                )}

                <div className={styles.boxesGrid}>
                  {visibleBoxes.map((box) => (
                    <div
                      key={box.id}
                      className={`${styles.boxCard} ${selectedBox?.id === box.id ? styles.selected : ''}`}
                      onClick={() => handleBoxSelect(box.id)}
                    >
                      <div className={styles.boxCheckbox}>
                        <input
                          type="checkbox"
                          checked={selectedBoxes.includes(box.id)}
                          onChange={() => handleCheckboxChange(box.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className={styles.boxHeader}>
                        <h3 className={styles.boxName}>{box.name}</h3>
                        <span className={`${styles.boxStatus} ${box.is_open ? styles.open : styles.closed}`}>
                          {box.is_open ? 'Открыта' : 'Закрыта'}
                        </span>
                      </div>
                      <div className={styles.boxStats}>
                        <div className={styles.boxStat}>
                          <span className={styles.boxStatLabel}>Упаковано</span>
                          <span className={styles.boxStatValue}>
                            {box.packaged_equipment ? box.packaged_equipment.length : 0}
                          </span>
                        </div>
                        <div className={styles.boxStat}>
                          <span className={styles.boxStatLabel}>Лимит</span>
                          <span className={styles.boxStatValue}>{MAX_EQUIPMENTS_PER_BOX}</span>
                        </div>
                      </div>
                      <button
                        className={styles.viewBoxButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBoxSelect(box.id);
                          setIsPackagedPanelOpen(true);
                        }}
                        title="Просмотр содержимого коробки"
                      >
                        <Eye size={16} />
                      </button>
                      <div className={styles.boxProgress}>
                        <div
                          className={styles.boxProgressBar}
                          style={{
                            width: `${
                              (box.packaged_equipment ? box.packaged_equipment.length : 0) /
                              MAX_EQUIPMENTS_PER_BOX *
                              100
                            }%`,
                            backgroundColor: box.is_open ? '#0f766e' : '#64748b',
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {boxesPage < maxBoxPage && (
                  <button
                    className={styles.boxesNavButton}
                    onClick={nextBoxPage}
                    aria-label="Следующие коробки"
                  >
                    <ChevronRight size={24} />
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.emptyBoxes}>
                <div className={styles.emptyIcon}>
                  <Box size={48} />
                </div>
                <p>Нет созданных коробок</p>
                <button
                  className={`${styles.actionButton} ${styles.createButton}`}
                  onClick={createBox}
                  disabled={isBoxCreating}
                >
                  {isBoxCreating ? (
                    <RefreshCw size={18} className={styles.spinIcon} />
                  ) : (
                    <Plus size={18} />
                  )}
                  <span>Создать первую коробку</span>
                </button>
              </div>
            )}

            {boxes.length > 0 && (
              <div className={styles.boxPagination}>
                {Array.from({ length: Math.ceil(boxes.length / boxesPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.paginationDot} ${boxesPage === index ? styles.active : ''}`}
                    onClick={() => setBoxesPage(index)}
                    aria-label={`Страница ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {selectedBox && (
              <div className={styles.boxActions}>
                {selectedBox.is_open ? (
                  <button
                    className={`${styles.actionButton} ${styles.closeButton}`}
                    onClick={closeSelectedBox}
                    disabled={isBoxClosing}
                  >
                    {isBoxClosing ? (
                      <RefreshCw size={18} className={styles.spinIcon} />
                    ) : (
                      <Box size={18} />
                    )}
                    <span>Закрыть коробку</span>
                  </button>
                ) : (
                  <button
                    className={`${styles.actionButton} ${styles.openButton}`}
                    onClick={openSelectedBox}
                    disabled={isBoxOpening}
                  >
                    {isBoxOpening ? (
                      <RefreshCw size={18} className={styles.spinIcon} />
                    ) : (
                      <Box size={18} />
                    )}
                    <span>Открыть коробку</span>
                  </button>
                )}

                {packagedEquipments.length === 0 && (
                  <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={deleteEmptyBox}
                    disabled={isBoxDeleting}
                  >
                    {isBoxDeleting ? (
                      <RefreshCw size={18} className={styles.spinIcon} />
                    ) : (
                      <Trash2 size={18} />
                    )}
                    <span>Удалить пустую коробку</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {selectedBox && (
            <div className={styles.scanningSection}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  <Barcode size={20} className={styles.sectionIcon} />
                  Сканирование
                </h2>
                {selectedBox && (
                  <div className={styles.boxInfo}>
                    <span className={styles.boxName}>{selectedBox.name}</span>
                    <span className={`${styles.boxStatus} ${selectedBox.is_open ? styles.open : styles.closed}`}>
                      {selectedBox.is_open ? 'Открыта' : 'Закрыта'}
                    </span>
                  </div>
                )}
              </div>

              <div className={`${styles.scannerContainer} ${scanError ? styles.error : ''}`}>
                <div className={styles.scannerInputWrapper}>
                  <Barcode size={20} className={styles.scannerIcon} />
                  <input
                    type="text"
                    placeholder="Отсканируйте или введите серийный номер..."
                    value={scannedSerial}
                    onChange={handleSerialInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && scannedSerial.trim()) {
                        e.preventDefault();
                        handleScanSerial();
                      }
                    }}
                    className={styles.scannerInput}
                    ref={serialInputRef}
                    disabled={!selectedBox || !selectedBox.is_open || isScanning}
                  />
                </div>

                {!selectedBox.is_open && (
                  <div className={styles.boxClosedWarning}>
                    <AlertCircle size={20} />
                    <span>Коробка закрыта. Откройте коробку для сканирования.</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.footerActions}>
            {selectedBox && !selectedBox.is_open && (
              <button
                onClick={shipSingleBox}
                className={`${styles.actionButton} ${styles.shipButton}`}
                disabled={selectedBox.is_open}
              >
                <Truck size={20} />
                <span>Передать эту коробку на отправку</span>
              </button>
            )}

            {selectedBoxes.length > 0 && (
              <button
                onClick={shipSelectedBoxes}
                className={`${styles.actionButton} ${styles.shipButton}`}
              >
                <Truck size={20} />
                <span>Передать выбранные на отправку ({selectedBoxes.length})</span>
              </button>
            )}

            <button
              onClick={shipBoxes}
              className={`${styles.actionButton} ${styles.shipButton}`}
              disabled={boxes.some((box) => box.is_open) || boxes.length === 0}
            >
              <Truck size={20} />
              <span>Передать все коробки на отправку</span>
            </button>

            {boxes.some((box) => box.is_open) && (
              <div className={styles.shipWarning}>
                <AlertCircle size={16} />
                <span>Закройте все коробки перед отправкой</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.readySection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <Package size={20} className={styles.sectionIcon} />
                Оборудование для упаковки
                <span className={styles.countBadge}>{filteredReadyEquipments.length}</span>
              </h2>
            </div>

            <div className={styles.readyContent}>
              <div className={styles.searchContainer}>
                <Search size={18} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Поиск по серийному номеру или модели..."
                  value={readyFilter}
                  onChange={(e) => setReadyFilter(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <div className={styles.equipmentListContainer} ref={readyListContainerRef}>
                {filteredReadyEquipments.length === 0 ? (
                  <div className={styles.emptyList}>
                    <div className={styles.emptyIcon}>
                      <Package size={48} />
                    </div>
                    <p>Нет оборудования для упаковки</p>
                  </div>
                ) : (
                  <div className={styles.virtualListContainer}>
                    <AutoSizer>
                      {({ width, height }) => (
                        <FixedList
                          height={height || 400}
                          itemCount={filteredReadyEquipments.length}
                          itemSize={70}
                          width={width}
                          itemData={filteredReadyEquipments}
                          onItemsRendered={({ visibleStopIndex }) => {
                            if (
                              visibleStopIndex >= filteredReadyEquipments.length - 5 &&
                              hasMoreReady
                            ) {
                              loadMoreReadyEquipments();
                            }
                          }}
                          className={styles.virtualList}
                        >
                          {EquipmentRow}
                        </FixedList>
                      )}
                    </AutoSizer>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PackagedEquipmentPanel
        isOpen={isPackagedPanelOpen}
        onClose={handlePackagedPanelClose}
        selectedBox={selectedBox}
        filteredPackagedEquipments={filteredPackagedEquipments}
        packagedFilter={packagedFilter}
        onFilterChange={setPackagedFilter}
        reportTemplates={reportTemplates}
        handleDownloadReport={handleDownloadReport}
        handleEquipmentHistoryClick={handleEquipmentHistoryClick}
        handleRemoveEquipment={handleRemoveEquipment}
        handleEmptyBox={handleEmptyBox}
      />

      <ShipmentDocumentsModal
        data={shipmentDocuments}
        loading={shipmentDocumentsLoading}
        onClose={() => {
          setShipmentDocuments(null);
          if (!isPanel) navigate('/packaging');
        }}
      />

      {/* Equipment History Panel */}
      <EquipmentHistoryPanel
        isOpen={historyPanelOpen}
        onClose={closeHistoryPanel}
        equipmentId={selectedEquipmentId}
        title="История оборудования"
      />
    </div>
  );
});

PackagingDetails.displayName = 'PackagingDetails';

export default PackagingDetails;

const PackagedEquipmentPanel = memo(({
  isOpen,
  onClose,
  selectedBox,
  filteredPackagedEquipments,
  packagedFilter,
  onFilterChange,
  reportTemplates,
  handleDownloadReport,
  handleEquipmentHistoryClick,
  handleRemoveEquipment,
  handleEmptyBox,
}) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  return (
    <div className={`${styles.packagedDrawer} ${isOpen ? styles.open : ''}`}>
      <div className={styles.packagedDrawerOverlay} onClick={onClose} />
      <div className={styles.packagedDrawerPanel}>
        <div className={styles.packagedDrawerHeader}>
          <div className={styles.packagedPanelHeader}>
            <span className={styles.drawerTitle}>Упакованное оборудование</span>
            <span className={styles.countBadge}>{filteredPackagedEquipments.length}</span>
            {reportTemplates.length > 0 && selectedBox && (
              <ReportDropdown
                boxId={selectedBox.id}
                reportTemplates={reportTemplates}
                handleDownloadReport={handleDownloadReport}
                customerId={selectedBox.customer?.id}
              />
            )}
          </div>
          <button className={styles.drawerCloseButton} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {selectedBox?.is_open && filteredPackagedEquipments.length > 0 && (
          <div className={styles.bulkBoxActions}>
            <button className={styles.emptyBoxButton} onClick={handleEmptyBox}>
              <Eraser size={17} /> Убрать всё из коробки
            </button>
            <span>Все аппараты вернутся на этап «На упаковке».</span>
          </div>
        )}

        <div className={styles.packagedPanelBody}>
          {selectedBox && (
            <div className={styles.packagedPanelBoxInfo}>
              <span className={styles.boxName}>{selectedBox.name}</span>
              <span className={`${styles.boxStatus} ${selectedBox.is_open ? styles.open : styles.closed}`}>
                {selectedBox.is_open ? 'Открыта' : 'Закрыта'}
              </span>
            </div>
          )}

          <div className={styles.searchContainer}>
            <Search size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Поиск по серийному номеру или модели..."
              value={packagedFilter}
              onChange={(e) => onFilterChange(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={`${styles.modalEquipmentList} ${styles.packagedPanelList}`}>
            {filteredPackagedEquipments.length === 0 ? (
              <div className={styles.emptyList}>
                <div className={styles.emptyIcon}>
                  <Package size={48} />
                </div>
                <p>Нет упакованного оборудования</p>
              </div>
            ) : (
              <ul className={styles.equipmentList}>
                {filteredPackagedEquipments.map((eq) => (
                  <li key={eq.id} className={styles.equipmentItem}>
                    <div className={styles.equipmentInfo}>
                      <span className={styles.serialNumber}>{eq.serial_number}</span>
                      <span className={styles.modelName}>{eq.equipment_model}</span>
                      <span className={styles.statusBadge}>
                        {eq.repair_status_display || 'Не установлен'}
                      </span>
                    </div>
                    <div className={styles.equipmentActions}>
                      <button
                        className={styles.historyButton}
                        onClick={(e) => handleEquipmentHistoryClick(eq.id, e)}
                        title="Показать историю оборудования"
                      >
                        <History size={16} />
                      </button>
                      <button
                        className={styles.removeButton}
                        onClick={() => handleRemoveEquipment(eq.id)}
                        title="Удалить из коробки"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

PackagedEquipmentPanel.displayName = 'PackagedEquipmentPanel';

export const ShipmentDocumentsModal = ({ data, loading, onClose }) => {
  const [labelFormat, setLabelFormat] = useState('a4');
  const [missingAddress, setMissingAddress] = useState('');
  if (!data && !loading) return null;

  const printLabels = () => {
    if (!data) return;
    if (data.boxes.some((box) => !box.destination_address_complete) && !missingAddress.trim()) {
      toast.error('Укажите полный адрес назначения для бланков коробок');
      return;
    }
    const body = data.boxes.map((box) => `
      <section class="label">
        <h1>${escapeHtml(box.customer)}</h1>
        <p><strong>Адрес:</strong> ${escapeHtml(box.destination_address_complete ? box.destination_address : missingAddress.trim())}</p>
        <p><strong>Заявка:</strong> ${escapeHtml(box.repair_request || '—')}</p>
        <p><strong>Партия:</strong> ${escapeHtml(data.batch_number)}</p>
        <p><strong>Оборудование:</strong> ${escapeHtml(box.equipment_count)} шт.</p>
        <div class="big">Коробка ${box.sequence} из ${box.total_boxes}</div>
      </section>`).join('');
    openPrintWindow(
      `Маркировка ${data.batch_number}`,
      body,
      labelFormat === 'thermal' ? 'size: 100mm 100mm; margin: 4mm;' : 'size: A4; margin: 12mm;',
      labelFormat === 'thermal'
        ? '.label { width: 92mm; height: 92mm; padding: 4mm; gap: 2mm; break-after: page; } .label:last-child { break-after: auto; } .label h1 { font-size: 12pt; margin: 0; } .label p { font-size: 9pt; line-height: 1.2; } .label .big { font-size: 14pt; margin-top: 1mm; }'
        : '.label { height: 130mm; break-after: auto; } .label:nth-child(2n) { break-after: page; } .label:last-child { break-after: auto; }'
    );
  };

  const printAct = () => {
    if (!data) return;
    const rows = data.equipment.map((equipment, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(equipment.equipment_model)}</td><td>${escapeHtml(equipment.serial_number)}</td><td>${escapeHtml(equipment.repair_status)}</td></tr>`).join('');
    openPrintWindow(
      `Акт ПП ${data.batch_number}`,
      `<h1>Акт приёма-передачи № ${escapeHtml(data.batch_number)}</h1><p><strong>Заказчик:</strong> ${escapeHtml(data.customer)}</p><p><strong>Сервисный центр:</strong> ${escapeHtml(data.service_center)}</p><p><strong>Коробок:</strong> ${data.total_boxes}; <strong>оборудования:</strong> ${data.total_equipment}</p><table><thead><tr><th>№</th><th>Модель</th><th>Серийный номер</th><th>Статус</th></tr></thead><tbody>${rows}</tbody></table>`,
    );
  };

  const downloadAct = async () => {
    const template = data?.acceptance_transfer_template;
    if (!template) {
      toast.error('Для проекта не настроен шаблон акта приёма-передачи');
      return;
    }
    try {
      const response = await api.get('/reports/generate/', {
        params: { template_id: template.id, batch_number: data.batch_number },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Акт ПП ${data.batch_number}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось сформировать акт');
    }
  };

  return (
    <div className={styles.shipmentDocsOverlay} role="dialog" aria-modal="true">
      <div className={styles.shipmentDocsModal}>
        <div className={styles.shipmentDocsHeader}>
          <div><span>Партия подготовлена</span><h2>{data?.batch_number || 'Подготовка документов…'}</h2></div>
          <button onClick={onClose} aria-label="Закрыть"><X /></button>
        </div>
        {loading || !data ? <div className={styles.shipmentDocsLoading}>Подготавливаем бланки и акт…</div> : <>
          <div className={styles.shipmentDocsSummary}>
            <div><small>Заказчик</small><strong>{data.customer}</strong></div>
            <div><small>Коробок</small><strong>{data.total_boxes}</strong></div>
            <div><small>Оборудования</small><strong>{data.total_equipment}</strong></div>
          </div>
          <div className={styles.labelFormatRow}>
            <label><input type="radio" checked={labelFormat === 'a4'} onChange={() => setLabelFormat('a4')} /> A4</label>
            <label><input type="radio" checked={labelFormat === 'thermal'} onChange={() => setLabelFormat('thermal')} /> Термопринтер 100×100</label>
          </div>
          {data.boxes.some((box) => !box.destination_address_complete) && (
            <label className={styles.missingAddressField}>
              Полный адрес назначения для коробок без адреса в заявке
              <input
                type="text"
                value={missingAddress}
                onChange={(event) => setMissingAddress(event.target.value)}
                placeholder="Город, улица, дом, получатель"
              />
            </label>
          )}
          <div className={styles.shipmentDocsActions}>
            <button onClick={printLabels} disabled={data.boxes.some((box) => !box.destination_address_complete) && !missingAddress.trim()}><Printer size={18} /> Печать бланков коробок</button>
            <button onClick={printAct}><Printer size={18} /> Печать акта A4</button>
            <button onClick={downloadAct} disabled={!data.acceptance_transfer_template}><FileSpreadsheet size={18} /> Скачать акт XLSX</button>
          </div>
          {!data.acceptance_transfer_template && <p className={styles.templateWarning}>Шаблон акта для проекта пока не установлен. Бланки и печать доступны, скачивание XLSX появится после настройки шаблона.</p>}
        </>}
      </div>
    </div>
  );
};
